import loadable from '@loadable/component';
import { app_download_banner, iPhone } from 'media';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { AppStoreWhiteBg, PlayStoreWhiteBg } from '@travel/icons/ui';
import { ADJUST_APP_LINK } from '@travel/traveler-core/constants/common';
import {
  useDeviceType,
  useDialogHandler,
  useShortUrlQr,
  useTranslation,
  DESKTOP_SCREEN,
} from '@travel/traveler-core/hooks';
import { getRanData, getShortUrl } from '@travel/traveler-core/store/ranStore/selectors';
import { addRanDataToAppUrl } from '@travel/traveler-core/utils/helper';

import Dialog from 'components/Dialog';

import { Translate } from 'core/translate';

import styles from './appDownloadBanner.module.scss';

const DialogContentAppStore = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-DialogContentAppStore" */
      '@travel/traveler-core/components/AppDownload/DialogContentAppStore'
    ),
  { ssr: false },
);
const DialogContentPlayStore = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-DialogContentPlayStore" */
      '@travel/traveler-core/components/AppDownload/DialogContentPlayStore'
    ),
  { ssr: false },
);

function AppDownloadBanner() {
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const [storeName, setStoreName] = useState('');
  const deviceType = useDeviceType();
  const isDesktop = deviceType === DESKTOP_SCREEN;
  const ranData = useSelector(getRanData);
  const { iOS: iOSShortUrl, android: androidShortUrl } = useSelector(getShortUrl);

  const iOsAppUrl = addRanDataToAppUrl(ADJUST_APP_LINK, true, ranData);
  const AndroidAppUrl = addRanDataToAppUrl(ADJUST_APP_LINK, false, ranData);

  const appStoreButtonLabel = useTranslation({ id: 'Traveler_Common.Get_The_App.Alt.App_Store' });
  const playStoreButtonLabel = useTranslation({
    id: 'Traveler_Common.Get_The_App.Alt.Google_Play',
  });

  const shouldCustomQRUrl = useShortUrlQr(storeName, isOpen);

  const handleAppDownloadModal = (store: string) => {
    if (isDesktop) {
      onOpen();
      setStoreName(store);
      return;
    }
    if (store === 'appStore') {
      window.open(iOsAppUrl, '_blank');
    }
    if (store === 'playStore') {
      window.open(AndroidAppUrl, '_blank');
    }
  };

  const handleModalClose = () => {
    onClose();
    setStoreName('');
  };

  return (
    <>
      <div className={styles.appDownload} data-testid="appDownloadBanner-wrapper">
        <div className={styles.mediaContent}>
          <div className={styles.image}>
            <img src={iPhone} alt={`iPhone`} loading={`lazy`} />
          </div>
          <div className={styles.content}>
            <div className={styles.downloadTitle}>
              <Translate id="Top.Banner.Enjoy_Enjoy_The_Best_Booking_Experience_On_Our_App" />
            </div>
            <div className={styles.downloadText}>
              <Translate id="Top.Banner.Download_Rakuten_Travel_App_Here" />
            </div>
            <div className={styles.downloadBtn}>
              <div className={styles.downloadAppStore}>
                <button
                  onClick={() => handleAppDownloadModal('appStore')}
                  data-testid="download-appStore"
                  className={styles.downloadBtn}
                  aria-label={appStoreButtonLabel}
                >
                  <AppStoreWhiteBg width="110px" height="32px" />
                </button>
              </div>
              <div className={styles.downloadPlayStore}>
                <button
                  onClick={() => handleAppDownloadModal('playStore')}
                  data-testid="download-playStore"
                  className={styles.downloadBtn}
                  aria-label={playStoreButtonLabel}
                >
                  <PlayStoreWhiteBg width="110px" height="32px" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.media}>
          <img src={app_download_banner} alt="" className={styles.media} loading={`lazy`} />
        </div>
      </div>
      <Dialog
        title={null}
        isOpen={isOpen}
        onClose={handleModalClose}
        hasEnableBack={false}
        isUseContentFlexBox={true}
        className={styles.appDownloadDialog}
        contentClassName={styles.dialogContent}
        wrapperClassName={styles.appDownloadDialog}
        isResetContentBottomSpacing={true}
      >
        {storeName === 'appStore' && (
          <DialogContentAppStore appQRUrl={shouldCustomQRUrl ? iOSShortUrl.url : null} />
        )}
        {storeName === 'playStore' && (
          <DialogContentPlayStore
            appUrl={AndroidAppUrl}
            appQRUrl={shouldCustomQRUrl ? androidShortUrl.url : null}
          />
        )}
      </Dialog>
    </>
  );
}

export default AppDownloadBanner;
